import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, ActivityIndicator, useWindowDimensions } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { AtmosphericStage, CandyIconButton } from '../components/primitives';
import { ActivityRunner } from '../components/ActivityRunner';
import { IActivity, EvaluatorRouter, KeyStage } from '@eduwrite/shared';
import { useDeviceStore } from '../store/useDeviceStore';
import { useStrokeStore } from '../store/useStrokeStore';
import { useHoldToConfirm } from '../hooks/useHoldToConfirm';
import { FreestyleService } from '../services/FreestyleService';
import { Star, Sparkles, ChevronLeft } from 'lucide-react-native';

type Props = StackScreenProps<RootStackParamList, 'FreestyleActive'>;

const evaluatorRouter = new EvaluatorRouter();

export const FreestyleActiveScreen: React.FC<Props> = ({ navigation, route }) => {
    const { keyStage, yearGroup, subject, strand, subjectLabel } = route.params;
    const { deviceId, serverUrl } = useDeviceStore();
    const { width, height } = useWindowDimensions();
    const isCompact = width < 600 || height < 750;

    const [activities, setActivities] = useState<IActivity[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [starsCount, setStarsCount] = useState(0);
    const [completedCount, setCompletedCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [celebrationText, setCelebrationText] = useState<string | null>(null);

    const questionStartTimeRef = useRef(Date.now());
    const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const starBounceAnim = useRef(new Animated.Value(1)).current;

    const currentActivity = activities[currentIndex] || null;

    // Load activities from server using resilient FreestyleService
    const fetchActivities = async (count = 5): Promise<IActivity[]> => {
        return FreestyleService.fetchActivities({
            yearGroup,
            subject,
            strand,
            count,
            customServerUrl: serverUrl || undefined,
        });
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            setIsLoading(true);
            const initial = await fetchActivities(6);
            if (mounted) {
                setActivities(initial);
                setIsLoading(false);
                questionStartTimeRef.current = Date.now();
            }
        })();
        return () => {
            mounted = false;
            if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
        };
    }, []);

    // Prefetch additional questions when near the end of queue
    useEffect(() => {
        if (activities.length > 0 && currentIndex >= activities.length - 2) {
            fetchActivities(5).then((more) => {
                if (more.length > 0) {
                    setActivities(prev => [...prev, ...more]);
                }
            });
        }
    }, [currentIndex, activities.length]);

    const handleExit = () => {
        if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
        useStrokeStore.getState().clearStrokes();

        if (completedCount > 0) {
            navigation.replace('FreestyleSummary', {
                questionsCount: completedCount,
                starsEarned: starsCount,
                subject: subjectLabel || subject,
                yearGroup,
            });
        } else {
            navigation.goBack();
        }
    };

    const exitHold = useHoldToConfirm({
        holdMs: 1200,
        onConfirm: handleExit,
    });

    const triggerStarBounce = () => {
        Animated.sequence([
            Animated.timing(starBounceAnim, { toValue: 1.4, duration: 150, useNativeDriver: true }),
            Animated.timing(starBounceAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
        ]).start();
    };

    const handleActivityComplete = async () => {
        if (!currentActivity) return;

        const durationMs = Date.now() - questionStartTimeRef.current;
        const structuredData = useStrokeStore.getState().structuredData as any;
        const strokes = useStrokeStore.getState().completedStrokes;

        const evaluableSubmission = {
            id: `freestyle-${Date.now()}`,
            activityId: currentActivity.id,
            studentId: 'freestyle',
            classId: 'freestyle',
            strokes,
            structuredData,
            finalText: typeof structuredData?.answer === 'string' ? structuredData.answer : undefined,
            createdAt: Date.now(),
        };

        let isCorrect = true;
        let score = 100;
        try {
            const evalResult = await evaluatorRouter.evaluate(evaluableSubmission, {
                id: currentActivity.id,
                type: currentActivity.type,
                mechanic: currentActivity.mechanic,
                config: currentActivity.config || {},
            });
            score = evalResult.score;
            isCorrect = evalResult.isCorrect ?? (score >= 70);
        } catch {
            isCorrect = true;
            score = 100;
        }

        // Celebrate & Reward
        setCompletedCount(prev => prev + 1);
        if (isCorrect) {
            setStarsCount(prev => prev + 1);
            triggerStarBounce();
            setCelebrationText('🌟 Brilliant! +1 Star!');
        } else {
            setCelebrationText('👍 Good effort! Keep going!');
        }

        // Non-blocking log to server
        if (deviceId) {
            FreestyleService.logCompletion({
                deviceId,
                keyStage,
                yearGroup,
                subject,
                strand: currentActivity.strand || strand,
                activityTitle: currentActivity.title,
                activityType: currentActivity.type,
                score,
                isCorrect,
                durationMs,
                customServerUrl: serverUrl || undefined,
            });
        }

        // Auto-advance to next question after brief celebration display
        autoAdvanceTimerRef.current = setTimeout(() => {
            autoAdvanceTimerRef.current = null;
            setCelebrationText(null);
            useStrokeStore.getState().clearStrokes();
            questionStartTimeRef.current = Date.now();
            setCurrentIndex(prev => prev + 1);
        }, 1200);
    };

    if (isLoading) {
        return (
            <AtmosphericStage>
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#22C55E" />
                    <Text style={styles.loadingText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        Getting your questions ready...
                    </Text>
                </View>
            </AtmosphericStage>
        );
    }

    if (!currentActivity) {
        return (
            <AtmosphericStage>
                <View style={styles.loadingContainer}>
                    <Text style={styles.loadingText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        No questions available right now.
                    </Text>
                    <CandyIconButton
                        icon={ChevronLeft}
                        onPress={() => navigation.goBack()}
                        accessibilityLabel="Go back"
                        accessibilityHint="Returns to the previous screen"
                    />
                </View>
            </AtmosphericStage>
        );
    }

    const yearNum = yearGroup.replace('y', 'Year ');
    const displayHeader = `${yearNum.toUpperCase()} • ${subjectLabel || 'Maths'}`;

    return (
        <AtmosphericStage>
            {/* Top Navigation Bar */}
            <View style={[styles.topBar, isCompact && styles.topBarCompact]}>
                <View style={styles.exitWrap}>
                    <CandyIconButton
                        icon={ChevronLeft}
                        onPressIn={exitHold.pressIn}
                        onPressOut={exitHold.pressOut}
                        accessibilityLabel="Exit practice"
                        accessibilityHint="Hold to finish and return"
                    />
                    {exitHold.isConfirming && (
                        <Text style={styles.holdHintText} allowFontScaling={true} maxFontSizeMultiplier={1.2}>
                            Keep holding...
                        </Text>
                    )}
                </View>

                <View style={[styles.subjectPill, isCompact && styles.subjectPillCompact]}>
                    <Text style={[styles.subjectPillText, isCompact && styles.subjectPillTextCompact]} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                        {displayHeader}
                    </Text>
                </View>

                <Animated.View style={[styles.starJar, { transform: [{ scale: starBounceAnim }] }]}>
                    <Star size={isCompact ? 20 : 24} color="#F59E0B" fill="#F59E0B" />
                    <Text style={[styles.starText, isCompact && styles.starTextCompact]} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        {starsCount}
                    </Text>
                </Animated.View>
            </View>

            {/* Celebration Floating Banner */}
            {celebrationText && (
                <View style={styles.celebrationBanner}>
                    <Sparkles size={20} color="#FFFFFF" />
                    <Text style={styles.celebrationBannerText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        {celebrationText}
                    </Text>
                </View>
            )}

            {/* Active Learning Stage Card */}
            <View style={[styles.cardContainer, isCompact && styles.cardContainerCompact]}>
                <ActivityRunner
                    key={currentActivity.id}
                    activity={currentActivity}
                    onComplete={handleActivityComplete}
                    keyStage={keyStage as KeyStage}
                />
            </View>
        </AtmosphericStage>
    );
};

const styles = StyleSheet.create({
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8,
        zIndex: 10,
    },
    topBarCompact: {
        paddingHorizontal: 12,
        paddingTop: 10,
        paddingBottom: 4,
    },
    exitWrap: {
        alignItems: 'center',
    },
    holdHintText: {
        fontSize: 11,
        fontWeight: '800',
        color: '#EA580C',
        marginTop: 2,
    },
    subjectPill: {
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        elevation: 2,
    },
    subjectPillCompact: {
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderRadius: 16,
    },
    subjectPillText: {
        fontSize: 15,
        fontWeight: '900',
        color: '#1E293B',
    },
    subjectPillTextCompact: {
        fontSize: 13,
    },
    starJar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFBEB',
        borderColor: '#FDE68A',
        borderWidth: 2,
        borderRadius: 20,
        paddingHorizontal: 14,
        paddingVertical: 6,
        gap: 6,
    },
    starText: {
        fontSize: 18,
        fontWeight: '900',
        color: '#D97706',
    },
    starTextCompact: {
        fontSize: 15,
    },
    celebrationBanner: {
        position: 'absolute',
        top: 72,
        alignSelf: 'center',
        backgroundColor: '#22C55E',
        borderRadius: 20,
        paddingHorizontal: 20,
        paddingVertical: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        zIndex: 20,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 6,
        elevation: 5,
    },
    celebrationBannerText: {
        fontSize: 16,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    cardContainer: {
        flex: 1,
        minHeight: 0,
        paddingHorizontal: 16,
        paddingBottom: 20,
        width: '100%',
        maxWidth: 800,
        alignSelf: 'center',
    },
    cardContainerCompact: {
        paddingHorizontal: 8,
        paddingBottom: 8,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
    },
    loadingText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1E293B',
    },
});
